import { useState, type FC } from 'react';
import { Download, DownloadDone } from '@eqi-investimentos/icons/web';
import {
  type IconProps,
  useToast,
  type UseToastProps,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import type { IconButtonProps } from '@/components/elements/IconButton';
import { IconButton } from '@/components/elements/IconButton';

export const ToastButton: FC<ToastButtonProps> = ({
  id,
  ariaLabel,
  buttonProps,
  iconProps,
  customToastsConfig,
  disableStartToast = false,
  icon = Download,
  successIcon = DownloadDone,
  onClick,
}) => {
  const t = useT();
  const toast = useToast({ isClosable: true });

  const toastsConfig = mountToastsConfig(t, id, customToastsConfig);

  const [isSuccess, setIsSuccess] = useState(false);

  const handleSuccess = () => {
    setIsSuccess(() => true);
    setTimeout(() => {
      setIsSuccess(() => false);
    }, 1500);
  };

  const handleClick = async () => {
    if (!disableStartToast) toast(toastsConfig.start);
    onClick().then((isSuccess) => {
      const tooltip = ['error', 'success'][Number(isSuccess)] as
        | 'error'
        | 'success';

      toast.close(`file-toast-${id}`);
      toast(toastsConfig[tooltip]);

      if (isSuccess) {
        handleSuccess();
      }
    });
  };

  return (
    <IconButton
      ariaLabel={ariaLabel}
      icon={isSuccess ? successIcon : icon}
      onButtonClick={handleClick}
      iconProps={{
        w: '1.75rem',
        h: '1.75rem',
        color: isSuccess ? 'content.brand' : 'content.primary',
        ...iconProps,
      }}
      buttonProps={buttonProps}
    />
  );
};

const mountToastsConfig = (
  t: (key: string) => string,
  id: string,
  customToastsConfig?: ToastsConfig
) =>
  ({
    start: {
      id: `file-toast-${id}`,
      duration: 3000,
      status: 'loading',
      title: t('screens.researchProgram.supplementary.download.start.title'),
      ...customToastsConfig?.start,
    } as UseToastProps,
    error: {
      description: t(
        'screens.researchProgram.supplementary.download.error.description'
      ),
      duration: 3000,
      status: 'error',
      title: t('screens.researchProgram.supplementary.download.error.title'),
      ...customToastsConfig?.error,
    } as UseToastProps,
    success: {
      description: t(
        'screens.researchProgram.supplementary.download.success.description'
      ),
      duration: 3000,
      status: 'success',
      title: t('screens.researchProgram.supplementary.download.success.title'),
      ...customToastsConfig?.success,
    } as UseToastProps,
  }) as const;

type ToastButtonProps = Pick<
  IconButtonProps,
  'ariaLabel' | 'buttonProps' | 'iconProps'
> & {
  id: string;
  customToastsConfig?: ToastsConfig;
  disableStartToast?: boolean;
  icon?: FC<IconProps>;
  successIcon?: FC<IconProps>;
  onClick: () => Promise<boolean>;
};

type ToastsConfig = {
  error?: Omit<UseToastProps, 'status'>;
  start?: Omit<UseToastProps, 'status'>;
  success?: Omit<UseToastProps, 'status'>;
};
